import React, { useEffect, useRef } from 'react'
import SubscriptionCard from 'components/SubscriptionCard/SubscriptionCard'
import { CardDetails } from 'components/SubscriptionCard/CardDetails'
import SubPageStyle from './Style'
import { Grid, Alert, Box, LinearProgress, Typography, Paper } from '@mui/material'
import Fade from '@mui/material/Fade'
import Spinner from 'components/Spinner/Spinner'
import { useSearchParams } from 'react-router-dom'
import { useUser } from 'hooks/useUser'

// Map URL parameters to actual Stripe Price IDs
const PLAN_URL_TO_PRICE_ID: Record<string, string> = {
  'plus_plan': 'price_1O2HvrAVTqhthTXdVMIyYa4v',          // Plus Monthly
  'plus_plan_year': 'price_1O2I24AVTqhthTXdbe75ROVO',     // Plus Yearly
  'professional_plan': 'price_1O2HwDAVTqhthTXd4Ur75tzP',  // Pro Monthly
  'professional_plan_year': 'price_1O2I2GAVTqhthTXdqtDO1ME7', // Pro Yearly
  'business_plan': 'price_1O2I0VAVTqhthTXd3bqlPzut',      // Business Monthly
  'business_plan_year': 'price_1O2I0VAVTqhthTXd3yv1akrY', // Business Yearly
}

export default function Subscription(): JSX.Element {
  const [checked] = React.useState(true)
  const [switchValue, setSwitchValue] = React.useState(true)
  const [isLoading, setIsLoading] = React.useState(false)
  const [searchParams] = useSearchParams()
  const plan = searchParams.get('plan')
  const firstTime = useRef(true)
  
  const { user, loading: userLoading } = useUser()

  const stripePayment = async (priceId: string): Promise<void> => {
    setIsLoading(true)
    try {
      const response = await fetch('/payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          price_id: priceId, // Changed from lookup_key to price_id
          customer_email: user?.email,
        }),
      })
      const result = await response.json()
      if (result.error) {
        setIsLoading(false)
        alert(result.error)
      } else {
        setIsLoading(false)
        window.location.href = result.url
      }
    } catch (e) {
      console.log(e)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (plan && firstTime.current) {
      firstTime.current = false
      
      // Get the price ID from the URL parameter
      const priceId = PLAN_URL_TO_PRICE_ID[plan]
      
      if (priceId) {
        void stripePayment(priceId)
      } else {
        console.error('Unknown plan parameter:', plan)
        setIsLoading(false)
      }
    }
  }, [plan])

  if (userLoading) {
    return (
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <Spinner />
      </div>
    )
  }

  const currentSubscription = user?.subscription
  const currentUsage = user?.usage

  return (
    <>
      <Fade in={checked} timeout={1500}>
        <SubPageStyle.PageContainer>
          <SubPageStyle.PageInfo>
            <SubPageStyle.PageTitle>
              Our pricing plan made simple
            </SubPageStyle.PageTitle>
            <SubPageStyle.PageSubtitle>
              All types of businesses need access to development resources, so
              we give you the option to decide how much you need to use
            </SubPageStyle.PageSubtitle>
          </SubPageStyle.PageInfo>

          {/* Current Subscription & Usage Status */}
          {currentSubscription && (
            <Box sx={{ mb: 4, mx: 'auto', maxWidth: '1000px' }}>
              {/* Subscription Status */}
              {currentSubscription.status !== 'FREE' && (
                <Alert 
                  severity={
                    currentSubscription.status === 'ACTIVE' ? 'success' : 
                    currentSubscription.status === 'CANCELED' ? 'warning' : 
                    'info'
                  }
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Current Plan: {currentSubscription.planDisplayName}
                    {currentSubscription.status === 'CANCELED' && ' (Canceled)'}
                    {currentSubscription.status === 'EXPIRED' && ' (Expired)'}
                  </Typography>
                  {currentSubscription.endDate && (
                    <Typography variant="body2">
                      {currentSubscription.status === 'CANCELED' 
                        ? `Access until: ${new Date(currentSubscription.endDate).toLocaleDateString()}`
                        : `Renews: ${new Date(currentSubscription.endDate).toLocaleDateString()}`
                      }
                    </Typography>
                  )}
                </Alert>
              )}

              {/* Usage Display */}
              {currentUsage && (
                <Paper 
                  elevation={2}
                  sx={{ 
                    p: 3, 
                    bgcolor: 'background.paper', 
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Monthly Usage ({currentUsage.currentMonth})
                  </Typography>
                  
                  <Grid container spacing={3}>
                    {/* Items Usage */}
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" fontWeight="500">Items Created</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {currentUsage.itemsCreated} / {
                              currentUsage.maxItemsPerMonth === -1 
                                ? '∞' 
                                : currentUsage.maxItemsPerMonth
                            }
                          </Typography>
                        </Box>
                        {currentUsage.maxItemsPerMonth !== -1 && (
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min((currentUsage.itemsCreated / currentUsage.maxItemsPerMonth) * 100, 100)}
                            sx={{ 
                              height: 8, 
                              borderRadius: 1,
                              backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            }}
                            color={currentUsage.itemsRemaining === 0 ? 'error' : 'primary'}
                          />
                        )}
                        {currentUsage.itemsRemaining === 0 && currentUsage.maxItemsPerMonth !== -1 && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                            ⚠️ Monthly limit reached
                          </Typography>
                        )}
                        {currentUsage.itemsRemaining > 0 && currentUsage.itemsRemaining < 5 && currentUsage.maxItemsPerMonth !== -1 && (
                          <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                            💡 {currentUsage.itemsRemaining} items remaining
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* AI Credits Usage */}
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" fontWeight="500">AI Credits Used</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {currentUsage.aiCreditsUsed} / {
                              currentUsage.maxAICreditsPerMonth === -1 
                                ? '∞' 
                                : currentUsage.maxAICreditsPerMonth.toLocaleString()
                            }
                          </Typography>
                        </Box>
                        {currentUsage.maxAICreditsPerMonth !== -1 && (
                          <LinearProgress 
                            variant="determinate" 
                            value={Math.min((currentUsage.aiCreditsUsed / currentUsage.maxAICreditsPerMonth) * 100, 100)}
                            sx={{ 
                              height: 8, 
                              borderRadius: 1,
                              backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            }}
                            color={
                              currentUsage.aiCreditsRemaining === 0 ? 'error' :
                              currentUsage.aiCreditsRemaining < 100 ? 'warning' : 
                              'primary'
                            }
                          />
                        )}
                        {currentUsage.aiCreditsRemaining > 0 && 
                         currentUsage.aiCreditsRemaining < 100 && 
                         currentUsage.maxAICreditsPerMonth !== -1 && (
                          <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                            💡 {currentUsage.aiCreditsRemaining} credits remaining
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </Box>
          )}

          <SubPageStyle.CardContainer>
            <Grid container spacing={2}>
              {CardDetails.map((item) => (
                <Grid item key={item.id} xs={12} sm={12} md={6} lg={4} xl={4}>
                  <SubscriptionCard
                    key={item.id}
                    cardTitle={item.cardTitle}
                    plan={item.plan}
                    stripePriceIds={item.stripePriceIds}
                    pricing={item.pricing}
                    features={item.features}
                    planDescription={item.planDescription}
                    setIsLoading={setIsLoading}
                    switchValue={switchValue}
                    setSwitchValue={setSwitchValue}
                  />
                </Grid>
              ))}
            </Grid>
          </SubPageStyle.CardContainer>

          {isLoading && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <Spinner />
            </div>
          )}
        </SubPageStyle.PageContainer>
      </Fade>
    </>
  )
}